import {
  initializeAuth,
  setAuthLoading,
  setSession,
  setUserStatus,
  signOut,
} from '@lumora/common';
import React, { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useAuthService } from '../../hooks/useAuthService';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import { useCachedApi } from '../../services/apiCache';
import { auditService } from '../../services/audit';
import { webSessionManager } from '../../services/auth/web-session';
import { sessionManager } from '../../services/sessionManager';
import { errorHandler } from '../../utils/errorHandler';
import { SessionTimeoutModal } from '../ui/SessionTimeoutModal';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const authService = useAuthService();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, userStatus } = useAppSelector(
    state => state.auth
  );
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const lastCheckedUserId = useRef<string | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Session timeout management
  const { extendSession } = useSessionTimeout({
    onWarning: () => setShowTimeoutWarning(true),
    onTimeout: async () => {
      setShowTimeoutWarning(false);
      if (user?.id) {
        await auditService.logSessionTimeout(user.id);
      }
      dispatch(signOut());
    },
  });

  useEffect(() => {
    // Fallback: ensure loading is set to false after 10 seconds in case of issues
    loadingTimeoutRef.current = setTimeout(() => {
      dispatch(setAuthLoading(false));
    }, 10000);

    // With persistSession: true enabled in Supabase config, the client will:
    // 1. Automatically persist the session in localStorage
    // 2. Auto-refresh tokens before they expire
    // 3. Restore the session on page load
    // We just need to initialize and listen for changes
    dispatch(initializeAuth()).catch((error: unknown) => {
      console.error('Failed to initialize auth:', error);
      dispatch(setAuthLoading(false));
    });

    // Listen for auth state changes
    const unsubscribe = authService.onSessionChange(async session => {
      try {
        // Clear the loading timeout since auth state is now determined
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }

        dispatch(setSession(session));
        dispatch(setAuthLoading(false));

        // Also update WebSessionManager when session is created
        if (session) {
          webSessionManager.initializeSession(session, true); // true for rememberMe to persist across browser restarts

          // Sync with DB using SessionManager (non-blocking)
          const { getSupabaseClient } = await import('@lumora/common');
          const {
            data: { session: supabaseSession },
          } = await getSupabaseClient().auth.getSession();

          if (supabaseSession) {
            // Fire and forget - don't block on DB operations
            sessionManager.onSessionStart(supabaseSession).catch(() => {
              // Silently ignore - DB session is supplementary
            });
          }
        } else {
          webSessionManager.clearSession();
          // Fire and forget - don't block on DB operations
          sessionManager.onSessionEnd().catch(() => {
            // Silently ignore - DB session is supplementary
          });
        }

        // Check user status after session is set, but only once per user
        if (session?.user && session.user.id !== lastCheckedUserId.current) {
          lastCheckedUserId.current = session.user.id;

          // Single query to get user status
          import('@lumora/common')
            .then(({ getSupabaseClient }) => {
              const supabase = getSupabaseClient();
              return supabase
                .from('user_profiles')
                .select('status')
                .eq('id', session.user.id)
                .single();
            })
            .then(({ data, error }) => {
              if (error) {
                console.warn('Failed to fetch user status:', error);
                // If it's a 403, the session might be invalid - don't set user status
                if (
                  error.code === 'PGRST301' ||
                  error.message?.includes('403')
                ) {
                  console.warn(
                    'Session may be invalid - skipping user status check'
                  );
                  return;
                }
                dispatch(setUserStatus(null));
                return;
              }

              if (!data) {
                console.warn('No user profile data found');
                dispatch(setUserStatus(null));
                return;
              }

              const status = data.status as
                | 'pending_verification'
                | 'onboarding'
                | 'active'
                | 'suspended';
              dispatch(setUserStatus(status));
            })
            .catch(error => {
              console.warn('Failed to check user status:', error);
              // If it's a 403, don't show error to user as session might be invalid
              if (
                error?.code === 'PGRST301' ||
                error?.message?.includes('403')
              ) {
                console.warn(
                  'Session may be invalid - skipping user status check'
                );
                return;
              }
              dispatch(setUserStatus(null));

              // Handle status check error
              errorHandler.handleAuthError(error, {
                customMessage:
                  'Unable to check your account status. Some features may be limited.',
                showToast: false, // Don't show toast for this non-critical error
              });
            });
        } else if (!session?.user) {
          lastCheckedUserId.current = null;
          dispatch(setUserStatus(null));
        }
      } catch (error) {
        console.error('Error in session change handler:', error);
        errorHandler.handleAuthError(error, {
          customMessage: 'Authentication error occurred. Please sign in again.',
        });
      }
    });

    return () => {
      unsubscribe();
      // Clear timeout on cleanup
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [dispatch]);

  // Listen for session refresh requests from WebSessionManager
  useEffect(() => {
    const handleSessionRefresh = async () => {
      try {
        console.log('🔄 Refreshing session...');
        // We need to cast authService to any because refreshSession is not in the interface yet
        // but we added it to the implementation
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (authService as any).refreshSession();
        if (error) {
          console.error('❌ Failed to refresh session:', error);
        } else {
          console.log('✅ Session refreshed successfully');
        }
      } catch (err) {
        console.error('❌ Error refreshing session:', err);
      }
    };

    window.addEventListener('sessionRefreshNeeded', handleSessionRefresh);

    return () => {
      window.removeEventListener('sessionRefreshNeeded', handleSessionRefresh);
    };
  }, [authService]);

  // Listen for session expiration events from WebSessionManager
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('⚠️ Session expired event received');
      dispatch(signOut());
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [dispatch]);

  // Listen for session revoked events from SessionManager
  useEffect(() => {
    const handleSessionRevoked = async () => {
      console.warn('🚫 Session revoked - signing out');
      // Clear local session state first
      webSessionManager.clearSession();
      sessionManager.clearCurrentSession();
      // Then sign out from Supabase
      await authService.signOut();
    };

    window.addEventListener('sessionRevoked', handleSessionRevoked);

    return () => {
      window.removeEventListener('sessionRevoked', handleSessionRevoked);
    };
  }, [authService]);

  const handleExtendSession = () => {
    extendSession();
    setShowTimeoutWarning(false);
  };

  const handleLogout = async () => {
    setShowTimeoutWarning(false);

    // Remove session from DB before signing out
    try {
      await sessionManager.onSessionEnd();
    } catch (error) {
      console.error('Error removing session from DB:', error);
    }

    await authService.signOut();
  };

  // Prefetch analytics data when authenticated
  const { prefetchAnalytics } = useCachedApi();

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      prefetchAnalytics(user.id);
    }
  }, [isAuthenticated, user, prefetchAnalytics]);

  // Check for inconsistent authentication state
  useEffect(() => {
    if (isAuthenticated && user && userStatus) {
      // Check for inconsistent state scenarios
      const checkConsistency = async () => {
        try {
          // Scenario: User is authenticated but has invalid status
          if (
            ![
              'pending_verification',
              'onboarding',
              'active',
              'suspended',
            ].includes(userStatus)
          ) {
            errorHandler.handleInconsistentState(
              'onboarding',
              {
                scenario: 'invalid_user_status',
                userId: user.id,
                userStatus,
              },
              {
                customMessage:
                  'We detected an issue with your account status. Please sign in again.',
                recoveryActions: [
                  {
                    label: 'Sign In Again',
                    action: () => {
                      dispatch(signOut());
                    },
                    primary: true,
                  },
                ],
              }
            );
          }
        } catch (error) {
          console.error('Error checking authentication consistency:', error);
        }
      };

      checkConsistency();
    }
  }, [isAuthenticated, user, userStatus, dispatch]);

  return (
    <>
      {children}
      <SessionTimeoutModal
        isOpen={showTimeoutWarning}
        onExtendSession={handleExtendSession}
        onLogout={handleLogout}
      />
    </>
  );
};
